import { ROUTES, servicePath, articlePath } from "./routes";
import { absoluteCanonical, buildBreadcrumbs, buildSchemaGraph, SITE_ORIGIN } from "./schemaOrg";
import { getArticleBySlug, getPublishedArticles } from "./articles";

export { SITE_ORIGIN, absoluteCanonical, buildBreadcrumbs };

export const SERVICE_SLUGS = ["social-media-management", "ads", "website", "consultanta"];

function truncate(text, max = 160) {
  if (!text) return "";
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trim()}…`;
}

function pagesSeoBlock(t, key) {
  const block = t?.pagesSeo?.[key];
  if (!block || typeof block !== "object") return { title: "", description: "" };
  return {
    title: String(block.title || "").trim(),
    description: String(block.description || "").trim(),
  };
}

function homeSeo(lang, t, brandingSeo) {
  const ov = pagesSeoBlock(t, "home");
  return {
    title: ov.title || brandingSeo.siteTitle,
    description: ov.description || brandingSeo.metaDescription,
    path: ROUTES.home,
  };
}

function staticPageSeo(lang, path, title, description, image) {
  const suffix = " | Social Destiny";
  const alreadySuffixed = /social destiny/i.test(String(title || ""));
  return {
    title: alreadySuffixed ? title : `${title}${suffix}`,
    description: truncate(description),
    path,
    image: image || undefined,
    type: image ? "article" : undefined,
  };
}

function withPageOverride(t, key, fallbackTitle, fallbackDescription) {
  const ov = pagesSeoBlock(t, key);
  return {
    title: ov.title || fallbackTitle,
    description: ov.description || fallbackDescription,
  };
}

/**
 * @param {string} pathname
 * @param {{ slug?: string }} params
 * @param {'ro'|'en'} lang
 * @param {object} t - merged translations
 * @param {object|null} serviceData - merged service page
 * @param {object} brandingSeo - branding.seo[lang] merged with defaults
 */
export function resolvePageSeo(pathname, params, lang, t, serviceData, brandingSeo) {
  const path = pathname.replace(/\/$/, "") || "/";

  if (path === ROUTES.home || path === "/") {
    return homeSeo(lang, t, brandingSeo);
  }

  if (path === ROUTES.services) {
    const ov = withPageOverride(
      t,
      "services",
      "Servicii",
      t.services_page?.subtitle || t.services_page?.title
    );
    return staticPageSeo(lang, ROUTES.services, ov.title, ov.description);
  }

  if (path.startsWith(`${ROUTES.services}/`)) {
    const slug = params.slug || path.split("/").pop();
    if (serviceData) {
      const title = String(serviceData.seoTitle || "").trim() || serviceData.title;
      const description =
        String(serviceData.seoDescription || "").trim() ||
        serviceData.lead ||
        serviceData.intro_body;
      return staticPageSeo(lang, servicePath(slug), title, description, serviceData.intro_image);
    }
  }

  if (path === ROUTES.articles) {
    const ov = withPageOverride(
      t,
      "articles",
      "Articole",
      t.articles_page?.subtitle || t.articles_page?.title
    );
    return staticPageSeo(lang, ROUTES.articles, ov.title, ov.description);
  }

  if (path.startsWith(`${ROUTES.articles}/`)) {
    const slug = params.slug || path.split("/").pop();
    const article = getArticleBySlug(t, slug);
    if (article) {
      return staticPageSeo(
        lang,
        articlePath(article.slug),
        article.seoTitle || article.title,
        article.seoDescription || article.excerpt || article.title,
        article.coverUrl
      );
    }
  }

  if (path === ROUTES.portfolio) {
    const ov = withPageOverride(
      t,
      "portfolio",
      "Portofoliu",
      t.portfolio?.subtitle || t.portfolio?.title
    );
    return staticPageSeo(lang, ROUTES.portfolio, ov.title, ov.description);
  }

  if (path === ROUTES.about) {
    const ov = withPageOverride(t, "about", "Despre noi", t.about?.lead || t.about?.title);
    return staticPageSeo(lang, ROUTES.about, ov.title, ov.description);
  }

  if (path === ROUTES.contact) {
    const ov = withPageOverride(t, "contact", "Contact", t.contact?.subtitle || t.contact?.title);
    return staticPageSeo(lang, ROUTES.contact, ov.title, ov.description);
  }

  if (path === ROUTES.privacy) {
    return staticPageSeo(
      lang,
      ROUTES.privacy,
      lang === "en" ? "Privacy Policy" : "Politica de confidențialitate",
      t.legal?.privacy?.title || "Social Destiny"
    );
  }

  if (path === ROUTES.terms) {
    return staticPageSeo(
      lang,
      ROUTES.terms,
      lang === "en" ? "Terms & Conditions" : "Termeni și condiții",
      t.legal?.terms?.title || "Social Destiny"
    );
  }

  if (path === ROUTES.cookies) {
    return staticPageSeo(
      lang,
      ROUTES.cookies,
      lang === "en" ? "Cookie Policy" : "Politica de cookie-uri",
      t.legal?.cookies?.title || "Social Destiny"
    );
  }

  return homeSeo(lang, t, brandingSeo);
}

export function buildJsonLd({ pathname, lang, t, serviceData, contactInfo, pageSeo }) {
  return buildSchemaGraph({
    pathname,
    lang,
    t,
    serviceData,
    contactInfo,
    pageSeo,
    origin: SITE_ORIGIN,
    articles: getPublishedArticles(t),
  });
}
